import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

const STEPS: {
  title: string;
  body: string;
  where?: [string, ...string[]];
  whereNote?: string;
  figure?: ReactNode;
}[] = [
  {
    title: 'Teams, roles and seats are three different things',
    body: 'A role says what a person may do to the workspace: Owner, Admin or Member. A seat is the right to be an active member at all. A team is a named group of people with an optional lead, which other features use to say who something belongs to. Changing one does not change the others. A team does not widen or narrow what anyone can see on its own; each feature decides how to use it. OKRs file team objectives under a team, and a CRM pipeline can name teams as the people allowed to open it.',
  },
  {
    title: 'See how many seats you have',
    body: 'Seats shows how many people are in use against your limit, how many seats remain, and how many invitations are still pending. A seat is an active membership. Deactivating someone frees theirs at once, and reactivating them takes one back, so it cannot be used to get around the limit. A new workspace starts with a limit of five seats at a contracted price of EUR 5 per seat per month. Revvly can change the limit and the price for your workspace, and a workspace with no limit set shows No limit set. The price is shown for information: nothing is invoiced from seats yet.',
    where: ['Org Settings', 'Seats'],
    whereNote:
      'Only workspace admins and owners can ask for more seats.',
  },
  {
    title: 'What happens at the limit',
    body: 'When every seat is in use, Invite User on the Users page is disabled and its tooltip says so, and the Users page shows the count as N of M seats used. Two ways out: free a seat by deactivating someone, or ask for more. If your limit is lowered below your current usage, nobody is signed out. The workspace shows how far over it is, and no one new can be added until usage is back under the limit.',
    where: ['Users'],
  },
  {
    title: 'Ask for more seats',
    body: 'Enter how many more you want and, if useful, a note, then choose Request seats. The request goes to Revvly, because the limit is a contract term and cannot be raised from the dashboard. You can have one open request at a time; the page then shows what you asked for and the date. Revvly changes the limit on its side and you will see the new number the next time the page loads.',
    where: ['Org Settings', 'Seats', 'More seats', 'Request seats'],
    whereNote: 'Admins and owners only.',
  },
  {
    title: 'Which invitations hold a seat',
    body: 'It depends on how the person was invited. An email invitation from the Users page creates the membership straight away, so it takes a seat the moment you send it, even before the person has signed in. An invitation sent from Slack holds no seat until the person accepts it, so you can invite more people than you have seats for and only the ones who accept count.',
    figure: (
      <Figure
        title="When an invitation takes a seat"
        caption="Email invitations count at once; Slack invitations count only once accepted."
      >
        <Flow
          steps={[
            { title: 'You invite someone' },
            {
              fork: [
                {
                  title: 'Email from Users',
                  text: 'Membership is created now. The seat is taken before first sign-in.',
                  tone: 'warn',
                },
                {
                  title: 'Invite from Slack',
                  text: 'No seat held. It is taken only when the person accepts.',
                  tone: 'accent',
                },
              ],
            },
          ]}
        />
      </Figure>
    ),
  },
  {
    title: 'Invite someone, and resend or copy a pending invitation',
    body: 'Choose Invite User, enter an email address, an optional name and a role, then Send Invitation. Until the person signs in for the first time their row shows an Invited badge. For those rows the row menu offers Resend Invite, which sends the same setup email again, and Copy Invitation Link, which copies the link for you to pass on yourself when email is not getting through. Both use the same setup link, which is valid for 24 hours and works once. They are offered only for people whose account was created by your invitation and who have not yet signed in; someone who already had a Revvly account is told to use Forgot password instead. The full invitation steps are in the add-user guide.',
    where: ['Users', 'row menu', 'Resend Invite'],
    whereNote:
      'Admins and owners only. Copy Invitation Link is in the same menu. See Add a team member for the first invitation.',
  },
  {
    title: 'Change someone’s role',
    body: 'Open the row menu and choose Change Role. The dialog offers Admin and Member: admins have full access, including managing team members, and members have limited access. Choose Save Role. A person who is already an Owner keeps that role unless you change it; the dialog shows it so that saving without a change does nothing. You cannot remove admin access from the last active administrator, whether by demoting them or deactivating them. The change is refused with a message telling you to promote another member first.',
    where: ['Users', 'row menu', 'Change Role'],
    whereNote: 'Admins and owners only. The same menu has Deactivate and Reactivate.',
  },
  {
    title: 'Create a team',
    body: 'Choose New team, give it a name, and optionally pick a lead and the first members. Only people who are active members of the workspace can be picked, and a person with a pending invitation can be, because their membership is already active. Two teams may share a name, so check the lead shown beside it. A team has no other settings: what it owns is decided by the features that use it. Anyone in the workspace can read the list of teams and their members; creating, renaming and archiving are for admins and owners.',
    where: ['Org Settings', 'Teams', 'New team'],
    whereNote:
      'Search appears once there are more than a few teams. Show archived teams lists the retired ones.',
  },
  {
    title: 'Set a lead and manage members',
    body: 'Open a team, then use Set lead or Change lead, and Add members. The lead can add and remove members of their own team without being an administrator; a team with no lead can only be managed by an admin or owner. A lead does not have to be a member, and the team page says so when they are not. Removing someone from a team changes only their place on it. Nothing they own is reassigned, so if work should move, change its owner first.',
    where: ['Org Settings', 'Teams', 'a team', 'Add members'],
    whereNote:
      'In a person picker, someone who has not signed in yet is shown with an Invited tag. They can still be chosen.',
  },
  {
    title: 'Archive a team, and what can block it',
    body: 'Archive a team you no longer use rather than deleting it. Its name keeps showing wherever it was used, such as past objectives, but it disappears from every picker, so nothing new can be assigned to it, and unarchiving restores it with its members. Archiving is refused while something still depends on the team, and the dialog lists each reason. Objectives owned by the team in any OKR cycle that is not closed yet block it, and the message names the cycle. A closed cycle does not. A live CRM pipeline that grants access through the team blocks it, and the message names the pipelines to re-scope first.',
    where: ['Org Settings', 'Teams', 'a team', 'Archive'],
    whereNote: 'Admins and owners only. An archived team also stops granting pipeline access.',
  },
  {
    title: 'Grant a function role',
    body: 'Function roles say what someone may do inside one feature, separately from billing and account administration. There are three. Cycle owner creates and publishes OKR cycles, writes company objectives and opens and closes scoring. Customer-success manager works with clients and sees the client fields limited to customer success. Customer-success team lead does the same and also opens the CSM workload page (see the CSM workload guide). Admins and owners hold every function role automatically and are listed as Via account admin. Use Grant to give a role to someone else, and Revoke to take it back.',
    where: ['Org Settings', 'Roles', 'Grant'],
    whereNote: 'Admins and owners only.',
  },
  {
    title: 'How teams feed OKRs',
    body: 'An objective is either a company objective or a team objective, and a team objective is filed under one of your teams. A team lead writes the objectives of the team they lead, and cycle owners write the company ones. Without a team there is nothing to file a team objective against, which is why the empty Teams page says an OKR cycle cannot be written until there is something to write it against. Objective and Key Result owners are told in the notification bell when something is assigned to them.',
  },
];

export function TeamsSeatsGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Set up teams, seats and invites — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Teams and seats</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Set up <em>teams, seats</em> and invites.
        </h1>
        <p className="dp-oneliner">
          Invite people, keep an eye on your seat limit, change roles, and group people into teams
          that OKRs and CRM pipelines can use. You need to be a workspace admin or owner for
          everything that changes something. For the OKR side, see the{' '}
          <Link to="/docs/guides/okrs">OKR guide</Link>; for a first invitation, see{' '}
          <Link to="/docs/guides/add-user">Add a team member</Link>.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.figure}
          {step.where && <UiPath steps={step.where} note={step.whereNote} />}
        </div>
      ))}
    </div>
  );
}
